import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useSEO, BASE } from "@/lib/seo";
import { IMAGES } from "@/lib/images";
import { RichText } from "@/components/site/RichText";
import ArticleShell, { H2, P, SeeAlso } from "@/components/site/ArticleShell";

const STEPS = [
  ["Se repérer", "Explorer les styles, les matériaux et les prix pratiqués. Lexique, courants et cotes donnent des repères avant tout contact."],
  ["Contacter l'atelier", "Un email ou un appel suffit. Un sculpteur en activité répond en général volontiers : parler de son travail fait partie du métier."],
  ["Voir l'œuvre", "Visite d'atelier si possible — c'est le plus sûr moyen de saisir le volume réel — ou demande de photographies détaillées sous plusieurs angles."],
  ["Vérifier l'authenticité", "Exiger le certificat signé : titre, année, matériaux, dimensions, numéro de tirage. Vérifier la signature et la cohérence avec le travail connu de l'artiste."],
  ["Convenir du prix", "Le prix d'atelier est souvent plus accessible que le prix de galerie, qui intègre une commission. Certains artistes proposent des paiements échelonnés."],
  ["Livraison & installation", "Œuvre lourde ou fragile : convenir par écrit du transport, de l'assurance et de l'installation. Un bronze se manipule avec des précautions précises."],
];

const CERTIFICAT = [
  "Nom et signature de l'artiste",
  "Titre de l'œuvre et année de création",
  "Technique et matériaux exacts",
  "Dimensions et poids",
  "Numéro de tirage (ex. 3/8) ou mention « pièce unique »",
  "Photographie de l'œuvre",
];

const COMPARE = [
  ["Prix", "Prix atelier, sans commission d'intermédiaire", "Commission de galerie incluse (souvent 30 à 50 %)"],
  ["Relation", "Échange direct avec le créateur, histoire de l'œuvre", "Conseil et médiation d'un professionnel du marché"],
  ["Choix", "L'œuvre dans son contexte de création, séries en cours", "Sélection de plusieurs artistes au même endroit"],
  ["Garanties", "Certificat remis par l'artiste lui-même", "Réseau, réputation et traçabilité de la galerie"],
];

export default function GuideAchatPage() {
  useSEO(
    "Guide d'Achat en Direct auprès d'un Sculpteur : Étapes, Certificats & Droits",
    "Comment acquérir une sculpture originale en direct d'atelier : certificat d'authenticité, cotation, épreuves d'artiste, fiscalité de l'art et livraison sécurisée.",
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Acheter une sculpture contemporaine en direct de l'atelier",
      description:
        "Guide pédagogique d'acquisition : étapes, certificat d'authenticité, comparaison achat direct vs galerie, prix et fiscalité de l'art en France.",
      inLanguage: "fr-FR",
      mainEntityOfPage: `${BASE}/guide-achat-direct-artiste`,
      author: { "@type": "Organization", name: "Sculpteur Français", url: `${BASE}` },
      publisher: { "@type": "Organization", name: "Sculpteur Français", url: `${BASE}` },
    },
    { type: "article" }
  );

  return (
    <ArticleShell
      testId="guide-achat-page"
      eyebrow="Guide pratique — acquérir"
      titleLines={["Acheter une sculpture", "en direct de l'atelier"]}
      lead="Acheter une œuvre directement auprès de son créateur est plus simple qu'on ne l'imagine. Parcours, vérifications et précautions — un angle informatif, pas commercial."
      image={{ ...IMAGES.gallery, to: "/lexique-sculpture/certificat-authenticite" }}
    >
      <H2>Pourquoi acheter en direct ?</H2>
      <P>
        Acheter en direct, c'est rencontrer l'œuvre là où elle naît. On
        comprend le geste, on entend l'histoire, on échange avec celui qui a
        modelé, taillé ou patiné la pièce. Le prix en est souvent adouci —
        aucune commission d'intermédiaire — et la relation qui s'établit fait
        partie de la valeur de l'acquisition.
      </P>

      <H2>Les six étapes d'une acquisition sereine</H2>
      <ol className="mt-8 divide-y divide-ink/10 border-y border-ink/10" data-testid="achat-steps">
        {STEPS.map(([title, text], i) => (
          <li key={title} className="grid gap-2 py-6 sm:grid-cols-12 sm:gap-6">
            <span className="font-mono text-sm text-bronze sm:col-span-2">{String(i + 1).padStart(2, "0")}</span>
            <span className="font-serif text-xl text-ink sm:col-span-4">{title}</span>
            <span className="text-sm font-light leading-relaxed text-smoke sm:col-span-6"><RichText text={text} /></span>
          </li>
        ))}
      </ol>

      <H2>Le certificat d'authenticité : la check-list</H2>
      <P>
        Document pivot de toute acquisition, le certificat protège l'acheteur
        et engage l'artiste. Il doit comporter au minimum :
      </P>
      <ul className="mt-6 grid gap-px border border-ink/15 bg-ink/10 sm:grid-cols-2" data-testid="certificat-checklist">
        {CERTIFICAT.map((item) => (
          <li key={item} className="flex items-start gap-3 bg-paper p-5">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-bronze" />
            <span className="text-sm font-light leading-relaxed text-body">{item}</span>
          </li>
        ))}
      </ul>

      <H2>Direct ou galerie : deux voies complémentaires</H2>
      <div className="mt-8 overflow-x-auto border border-ink/15" data-testid="direct-vs-galerie">
        <table className="w-full min-w-[640px] border-collapse text-left">
          <thead>
            <tr className="border-b border-ink/15 bg-cream">
              <th className="p-4 font-mono text-[11px] uppercase tracking-[0.18em] text-bronze">Critère</th>
              <th className="p-4 font-mono text-[11px] uppercase tracking-[0.18em] text-smoke">Achat en direct</th>
              <th className="p-4 font-mono text-[11px] uppercase tracking-[0.18em] text-smoke">Achat en galerie</th>
            </tr>
          </thead>
          <tbody>
            {COMPARE.map(([label, direct, galerie]) => (
              <tr key={label} className="border-b border-ink/10 last:border-0">
                <td className="p-4 font-serif text-lg text-ink">{label}</td>
                <td className="p-4 text-sm font-light leading-relaxed text-smoke">{direct}</td>
                <td className="p-4 text-sm font-light leading-relaxed text-smoke">{galerie}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <H2>Le prix d'une œuvre : comment il se construit</H2>
      <P>
        Le prix d'une sculpture tient à sa taille, son matériau — une fonte de
        bronze coûte bien plus cher qu'un moulage de résine —, son nombre
        d'exemplaires et la notoriété de l'artiste. La cotation, établie par
        les ventes publiques, sert de référence pour les artistes confirmés.
        Pour un sculpteur émergent, le prix d'atelier reflète surtout le temps
        de travail et les coûts de production.
      </P>

      <H2>Un mot de fiscalité</H2>
      <P>
        En France, l'achat d'œuvres d'artistes vivants peut ouvrir droit à des
        avantages : déduction fiscale pour les entreprises (article 238 bis AB
        du Code général des impôts), exonération d'IFI pour les œuvres d'art.
        Ces dispositifs évoluent : faites confirmer votre situation par un
        professionnel avant d'acheter.
      </P>

      <div className="mt-14 border border-bronze/30 bg-cream p-8 lg:p-10" data-testid="guide-cta">
        <p className="eyebrow">Une question pratique ?</p>
        <p className="mt-4 max-w-xl font-serif text-2xl leading-snug text-ink">
          Ce guide est éditorial et ne vend rien. Pour aller plus loin, posez
          votre question via la page contact.
        </p>
        <Link
          to="/contact"
          data-testid="guide-cta-contact"
          className="group mt-6 inline-flex items-center gap-3 bg-ink px-7 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-paper transition-colors duration-300 hover:bg-bronze"
        >
          Poser une question
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>

      <SeeAlso
        links={[
          { to: "/lexique-sculpture", label: "Épreuve d'artiste, tirage limité, cotation : le lexique" },
          { to: "/faq", label: "Les réponses aux questions fréquentes" },
          { to: "/materiaux-et-techniques", label: "Comprendre les matériaux" },
        ]}
      />
    </ArticleShell>
  );
}
